/* ============================================================================
 * BSD-SEARCH-CORE  -  portable Algolia search-intelligence engine
 * ----------------------------------------------------------------------------
 * WHAT THIS IS
 *   A theme-agnostic, dependency-free helper that improves Algolia search by
 *   (1) normalising the typed query (mm units, colloquial words),
 *   (2) mapping finish-family phrases to the bare trigger word the index's
 *       finish synonyms understand (so "brushed gold" reaches the whole gold
 *       family - Urban Brass, Tiger Bronze, …), and
 *   (3) detecting the product TYPE named in the query and boosting its
 *       product_type values via Algolia `optionalFilters` (a per-query,
 *       read-only, Grow-plan-safe boost - NOT a server-side Query Rule, which
 *       is plan-gated).
 *
 *   Type detection is MOST-SPECIFIC-WINS: a granular phrase ("bath waste",
 *   "shower head", "bottle trap") boosts that specific type AND suppresses the
 *   broader category word it contains, so "bath waste" returns wastes (not
 *   baths) and "bath" alone returns baths. A coarse category word on its own
 *   ("bath", "toilet") boosts the whole family.
 *
 *   It has NO DOM, NO CSS, NO network calls and NO Algolia credentials. It only
 *   transforms strings. The host theme owns the fetch, the config and the UI.
 *   That is what makes it portable: the maps describe the BSD *catalogue*
 *   (identical across every theme - all query the same `shopify_products`).
 *
 * PUBLIC API (window.BSDSearch)
 *   enhance(raw, opts?)          -> { query: String, optionalFilters: String[] }
 *                                   opts.skipFinish = don't broaden finish to family
 *                                   opts.intents = v1.7 size / floor-standing
 *                                   intents: adds facetFilters, extras[], size
 *   detectIntents(cleaned)       -> { query, facetFilters, optionalFilters, extras, size }
 *   variantForSize(variants, n)  -> the variant record titled "<n>mm ..." (or null)
 *   normalize(raw)               -> String           (cleaned query text)
 *   finishExpand(cleaned)        -> String           (finish phrase -> synonym trigger)
 *   detectBoosts(cleaned)        -> String[]         ("product_type:<value>" …)
 *   appendOptionalFilters(p, of) -> String           (append to a REST param string)
 *
 * REQUIREMENT
 *   The target Algolia index must facet on `product_type`. Governed boosts
 *   additionally use the `meta.custom.product_type.fields.name` facet (the
 *   governed Type metaobject, indexed by the Algolia app since 2026-08). If
 *   that declaration is ever lost (app reindex rewrites attributesForFaceting)
 *   the governed boosts degrade GRACEFULLY: optionalFilters on an undeclared
 *   attribute simply stop reordering - results stay correct, just unboosted.
 *
 * SELF-LIMITING PRINCIPLE (do not "fix" this - it is the safety property)
 *   `optionalFilters` only re-orders results that ALREADY match the query; it
 *   can never add non-matching products. So "bath mixer" still returns mixers.
 *
 * DATA-BOUNDED (known limits - a boost can't beat bad tags)
 *   Where product_type tagging is polluted, detection can't fully fix it, e.g.
 *   toilet buttons/connectors are mis-tagged `Toilet Suites`. Those need a
 *   Shopify data recategorise, not a search change.
 *
 * TO PORT TO ANOTHER THEME - see BSD-SEARCH-CORE-PORTING.md (copy file + wrap
 *   the Algolia products request with enhance()/appendOptionalFilters).
 *
 * MAINTENANCE
 *   Edit the maps only when the BSD catalogue's product_type values change.
 *   GRANULAR = curated sub-types (phrase -> product_type[]); add an entry to
 *   teach a new specific type. CATEGORY_BOOSTS = coarse families.
 *
 * GOVERNED BOOST LAYER (v1.5)
 *   The raw Shopify product_type is messy (6 different strings for wall-hung
 *   vanities, incl. empty). The BSD-Data governed Type metaobject
 *   (meta.custom.product_type) is the clean 28-value vocabulary and is near-
 *   complete for FIXTURES (vanities/baths/basins/mirrors/toilets/sinks).
 *   Where a concept is governed, its entry carries `g:` (governed names) and
 *   boosts the governed facet INSTEAD of raw product_type - never both, or
 *   sumOrFiltersScores would double-count products matching both fields and
 *   punish products whose raw type is stale (the wall-hung-vanity flip bug).
 *   Not-yet-governed categories (tapware/showers/accessories/...) keep raw
 *   `t:` boosts; migrate entries to `g:` as BSD-Data waves govern them.
 * ============================================================================ */
(function (root) {
  'use strict';

  /* ---- COARSE families - single trigger word -> product_type values -------- */
  var CATEGORY_BOOSTS = {
    bath:    ['Bathtubs', 'Freestanding Baths', 'Matte White Baths', 'Mercio Bathtubs', 'Ovia Bathtubs'],
    vanity:  ['Bathroom Vanities', 'Mercio Bathroom Vanities', 'Fienza Bathroom Vanities', 'Shop Fienza Vanities', 'Wall Hung Vanities', 'Vanities on Legs', 'Wall Hung Vanity', 'Vanities'],
    toilet:  ['Toilet Suites', 'In Wall Toilet Packages', 'Back-to-Wall Toilet Suites', 'Meir Toilet Suites', 'Ovia Toilet Suites', 'Fienza Toilet Suites', 'Back-to-Wall Care Toilets'],
    basin:   ['Bathroom Basins', 'Basin', 'Fienza Bathroom Basins', 'Madu Basins', 'Undermounted Basin Top', 'Matte White Basins'],
    shower:  ['Showers', 'Shower Heads', '2 in 1 Multifuction Showers', 'Hand Shower on Brackets', 'Nero Showers', 'Shower Mixers/Wall Mixers', 'Greens - Showers', 'Fienza Showers', 'Hand Shower on Rail', 'Modern National Showers', 'Shower Diverter Mixers', '2-in-1 Multi Function Showers', 'Meir Showers', 'Disabled Shower Products', 'Hand Shower Piece'],
    mirror:  ['Mirrors & Shaving Cabinets', 'Mercio Mirrors & Shaving Cabinets', 'LED Mirrors', 'Mirrors', 'Mirrors Only'],
    tapware: ['Bathroom Tapware', 'Fienza Bathroom Tapware', 'Greens - Tapwares', 'Basin Mixers', 'Shower Mixers/Wall Mixers', 'Nero Bathroom Tapware', 'Shower Diverter Mixers', 'Meir Bathroom Tapware', 'Wall Taps', 'Wall Basin Mixers', 'Tall Basin Mixers', 'Dual Mini Taps', 'Freestanding Bath Taps, Mixers & Tapware', 'Disability Basin Mixers', 'Modern National Bathroom Tapware', 'Kara 3 Piece Taps', 'Mecca Freestanding Bath Tap', 'Basin Mixer Taps'],
    sink:    ['Kitchen, Laundry and Butler Sinks', 'Linsol Sinks - Kitchen, Laundry & Butler Sinks', 'Nero Kitchen, Laundry & Butler Sinks', 'FINE FIRECLAY SINKS', 'Double Bowl Kitchen Sinks', 'Laundry Tubs', 'Granite Sinks', 'Cleaners Sink', 'Meir Kitchen & Laundry Sinks'],
    towel:   ['Towel Bars & Rails', 'Heated Towel Rails', 'Towel Rings', 'Heated Rails'],
    heating: ['Underfloor Heating', 'Modern National Heating & Cooling', 'Heating and Cooling', 'Heating Thermostats', 'Thermogroup - Heating & Cooling'],
    waste:   ['Shower Wastes', 'Floor wastes', 'Plug & Waste', 'Pop-up Wastes'],
    handle:  ['Cabinet Handles', 'Door Hardware', 'Commercial Door Hardware', 'Door Handles']
  };

  /* ---- v1.5: governed facet attribute + coarse overrides -------------------
   *   Categories listed here boost the governed Type facet INSTEAD of the raw
   *   CATEGORY_BOOSTS values (never both - see header). Only fixture
   *   categories with near-complete governed coverage belong here.
   *
   *   STACKING CONSTRAINT (probe-proven 2026-08-18): the governed metafield is
   *   a LIST - a multi-format product can carry several values (Zurich baths =
   *   Corner + Back to Wall + Freestanding + Multi-fit). Under
   *   sumOrFiltersScores every matched value adds its score (4 matches = 9 with
   *   the Ovia point), so a coarse category whose records are multi-valued
   *   catapults most-formats products over best products. OR-group nesting
   *   does NOT cap the sum (probed). Therefore: bath and vanity STAY on raw
   *   coarse boosts (their records are multi-valued); toilet/basin/mirror are
   *   governed (single-valued in practice). GRANULAR phrases are immune - they
   *   emit exactly ONE governed value, so nothing can stack. */
  var GOV_ATTR = 'meta.custom.product_type.fields.name';
  var CATEGORY_BOOSTS_GOV = {
    basin:  ['Above Counter Basin', 'Wall Hung Basin', 'Under Counter Basin', 'Semi Recessed Basin', 'Freestanding Basin', 'Inset Basin'],
    mirror: ['Mirror', 'LED Mirror'],
    toilet: ['Back to Wall Toilet', 'Wall Hung Toilet', 'In-Wall Cistern Toilet', 'Close Coupled Toilet', 'Smart Toilet']
  };

  var TRIGGERS = {
    bath: 'bath', baths: 'bath', tub: 'bath', tubs: 'bath',
    vanity: 'vanity', vanities: 'vanity',
    toilet: 'toilet', toilets: 'toilet',
    basin: 'basin', basins: 'basin',
    shower: 'shower', showers: 'shower',
    mirror: 'mirror', mirrors: 'mirror',
    tapware: 'tapware', tap: 'tapware', taps: 'tapware', mixer: 'tapware', mixers: 'tapware',
    sink: 'sink', sinks: 'sink',
    towel: 'towel', towels: 'towel',
    heating: 'heating', underfloor: 'heating',
    waste: 'waste', wastes: 'waste',
    handle: 'handle', handles: 'handle'
  };

  /* ---- GRANULAR sub-types - phrase(s) -> specific product_type values.
   *      Most-specific-wins: when a phrase matches, its words are CONSUMED so
   *      the coarse trigger inside it does NOT also fire. Processed longest
   *      phrase first (sorted at init). Phrases are normalised form
   *      (lowercase, alphanumerics + single spaces; "&"/"-" become spaces). */
  var GRANULAR = [
    // --- wastes & traps (the "bath waste != bath" class) ---
    { p: ['bottle trap'],                         t: ['Bottle Traps'] },
    { p: ['bath waste'],                          t: ['Plug & Waste', 'Pop-up Wastes', 'Floor wastes', 'Bath Parts'] },
    { p: ['shower waste'],                        t: ['Shower Wastes'] },
    { p: ['floor waste', 'floor grate'],          t: ['Floor wastes', 'Wedge Wire & Tile Insert Channels'] },
    { p: ['pop up waste', 'pop down waste'],      t: ['Pop-up Wastes', 'Plug & Waste'] },
    { p: ['plug and waste', 'plug waste'],        t: ['Plug & Waste', 'Pop-up Wastes'] },
    { p: ['basin waste'],                         t: ['Pop-up Wastes', 'Plug & Waste'] },
    // --- showers (sub-types of the broad "shower") ---
    { p: ['shower head', 'showerhead', 'shower rose', 'rain head', 'rain shower'], t: ['Shower Heads'] },
    { p: ['shower arm', 'ceiling arm', 'wall arm', 'gooseneck'], t: ['Shower Arms'] },
    { p: ['hand shower', 'handheld shower', 'hand held shower', 'hand piece', 'handpiece'], t: ['Hand Shower on Brackets', 'Hand Shower on Rail', 'Hand Shower Piece'] },
    { p: ['shower on rail', 'rail shower', 'sliding rail'], t: ['Hand Shower on Rail'] },
    { p: ['shower mixer', 'diverter mixer', 'shower diverter'], t: ['Shower Mixers/Wall Mixers', 'Shower Diverter Mixers'] },
    { p: ['shower screen'],                       t: ['Disabled Shower Products'] },
    // --- tapware sub-types ---
    { p: ['tall basin mixer', 'tower mixer'],     t: ['Tall Basin Mixers'] },
    { p: ['wall basin mixer', 'wall mixer'],      t: ['Wall Basin Mixers', 'Shower Mixers/Wall Mixers'] },
    { p: ['basin mixer'],                         t: ['Basin Mixers', 'Wall Basin Mixers', 'Tall Basin Mixers', 'Basin Mixer Taps'] },
    { p: ['kitchen mixer', 'kitchen tap', 'sink mixer'], t: ['Kitchen and Laundry Mixers', 'Pull out Kitchen Mixers'] },
    { p: ['wall tap', 'wall taps', 'wall top assembly'], t: ['Wall Taps'] },
    { p: ['bath spout', 'basin spout', 'wall spout', 'swivel spout', 'spout'], t: ['Bath and Basin Spout'] },
    { p: ['bath mixer', 'freestanding bath mixer', 'floor mixer'], t: ['Freestanding Bath Taps, Mixers & Tapware', 'Mecca Freestanding Bath Tap'] },
    // --- toilet-related accessories (route AWAY from Toilet Suites) ---
    { p: ['toilet roll holder', 'toilet paper holder', 'paper holder', 'roll holder'], t: ['Toilet Paper Holders'] },
    { p: ['toilet roll dispenser', 'paper dispenser'], t: ['Toilet Roll Dispensers'] },
    { p: ['toilet brush'],                        t: ['Toilet Brush Holders'] },
    { p: ['flush button', 'flush plate', 'push plate', 'push button', 'cistern button'], t: ['In Wall Cistern Buttons'] },
    { p: ['in wall cistern', 'concealed cistern'], t: ['In Wall Concealed Cisterns', 'In Wall Cistern Buttons'] },
    { p: ['in wall toilet', 'inwall toilet'],     t: ['In Wall Toilet Packages'] },
    // --- vanities sub-types (governed since v1.5) ---
    { p: ['wall hung vanity', 'wall hung vanities', 'floating vanity', 'wall mounted vanity'], g: ['Wall Hung Vanity'] },
    { p: ['floor standing vanity', 'freestanding vanity', 'free standing vanity'], g: ['Floor Standing Vanity'] },
    { p: ['vanity on legs', 'vanities on legs', 'vanity legs'], g: ['Vanity on Legs'] },
    { p: ['vanity top', 'vanity tops'],           g: ['Vanity Top'] },
    // --- basin sub-types (governed, new in v1.5) ---
    { p: ['above counter basin', 'countertop basin', 'counter top basin', 'benchtop basin'], g: ['Above Counter Basin'] },
    { p: ['wall hung basin', 'wall mounted basin'], g: ['Wall Hung Basin'] },
    { p: ['under counter basin', 'undercounter basin', 'undermount basin', 'under mount basin'], g: ['Under Counter Basin'] },
    { p: ['semi recessed basin'],                 g: ['Semi Recessed Basin'] },
    { p: ['freestanding basin', 'free standing basin'], g: ['Freestanding Basin'] },
    { p: ['inset basin', 'drop in basin'],        g: ['Inset Basin'] },
    // --- bath sub-types (governed, new in v1.5; freestanding bath repointed).
    //     NOTE: phrases must be in POST-normalize form - normalize() strips
    //     filler words ("to", "with", "and"), so "back to wall" arrives as
    //     "back wall" (same pattern as the existing 'plug waste' entry). ---
    { p: ['corner bath'],                         g: ['Corner Bath'] },
    { p: ['back wall bath', 'back to wall bath'], g: ['Back to Wall Bath'] },
    // --- toilet formats (governed, new in v1.5) ---
    { p: ['back wall toilet', 'back to wall toilet', 'btw toilet'], g: ['Back to Wall Toilet'] },
    { p: ['wall hung toilet', 'wall mounted toilet'], g: ['Wall Hung Toilet'] },
    { p: ['close coupled toilet'],                g: ['Close Coupled Toilet'] },
    { p: ['smart toilet'],                        g: ['Smart Toilet'] },
    // --- kitchen sink bowl formats (governed, new in v1.5; coarse "sink" stays
    //     raw - laundry tubs are not in the governed sink vocabulary) ---
    { p: ['single bowl sink'],                    g: ['Single Bowl Sink'] },
    { p: ['double bowl sink', 'double sink'],     g: ['Double Bowl Sink'] },
    { p: ['sink drainboard', 'sink with drainboard', 'drainboard sink', 'drainer sink'], g: ['Sink with Drainboard'] },
    // --- mirrors / cabinets (governed since v1.5) ---
    { p: ['shaving cabinet', 'shaving cabinets'], g: ['Shaving Cabinet', 'LED Shaving Cabinet'] },
    /* v1.7: "mirror cabinet" is what many shoppers call a shaving cabinet; the
       coarse "mirror" trigger used to boost plain mirrors instead (Zurich's
       Mirror Cabinet sat at #71). Most-specific-wins consumes both words. */
    { p: ['mirror cabinet', 'mirror cabinets', 'mirrored cabinet', 'medicine cabinet', 'medicine cabinets'], g: ['Shaving Cabinet', 'LED Shaving Cabinet'] },
    { p: ['led mirror', 'led mirrors'],           g: ['LED Mirror'] },
    // --- accessories ---
    { p: ['heated towel rail', 'heated rail'],    t: ['Heated Towel Rails', 'Heated Rails'] },
    { p: ['towel rail', 'towel bar'],             t: ['Towel Bars & Rails', 'Heated Towel Rails'] },
    { p: ['towel ring'],                          t: ['Towel Rings'] },
    { p: ['robe hook'],                           t: ['Robe Hooks', 'Hooks'] },
    { p: ['soap dispenser'],                      t: ['Soap Dispenser'] },
    { p: ['soap dish'],                           t: ['Soap Dish Holders'] },
    { p: ['grab rail', 'grab bar', 'back rest'],  t: ['Grab Rails & Back Rests', 'Disabled Grab Rails'] },
    { p: ['bath shelf', 'shower shelf', 'shelf', 'shelves'], t: ['Shelves', 'Bath Shelves'] },
    // --- sinks ---
    { p: ['kitchen sink'],                        t: ['Kitchen, Laundry and Butler Sinks', 'Double Bowl Kitchen Sinks', 'Granite Sinks'] },
    { p: ['laundry tub', 'laundry sink'],         t: ['Laundry Tubs', 'Kitchen, Laundry and Butler Sinks'] },
    { p: ['butler sink', 'fireclay sink'],        t: ['FINE FIRECLAY SINKS', 'Kitchen, Laundry and Butler Sinks'] },
    // --- heating / baths ---
    { p: ['underfloor heating', 'under floor heating', 'heated floor'], t: ['Underfloor Heating'] },
    { p: ['freestanding bath', 'free standing bath'], g: ['Freestanding Bath'] },
    // --- bidet ---
    { p: ['bidet spray', 'bidet shattaf', 'douche spray'], t: ['Bidet Sprays'] }
  ];

  /* sort granular longest-phrase-first so "tall basin mixer" beats "basin mixer" beats coarse "basin" */
  function maxLen(g) { var m = 0, i, n; for (i = 0; i < g.p.length; i++) { n = g.p[i].split(' ').length; if (n > m) m = n; } return m; }
  GRANULAR.sort(function (a, b) { return maxLen(b) - maxLen(a); });

  /* ---- single-token colloquialisms the index can't resolve on its own.
   *      tub/tubs are deliberately NOT rewritten here - doing so mangled
   *      "laundry tub" -> "laundry bath". Instead tub/tubs are a TRIGGER (boost
   *      bath, below) and a global synonym tub->bathtub handles matching. */
  var WORD_EXPAND = { loo: 'toilet', wc: 'toilet', dunny: 'toilet' };
  var PHRASE_EXPAND = { 'gun metal': 'gunmetal' };

  /* ---- brand word(s) -> the (often duplicated) vendor strings on the index.
   *      Used to boost a named brand, and - crucially - to know when NOT to
   *      promote Ovia (only promote Ovia when the shopper named no brand). */
  var BRANDS = [
    { p: ['ovia'], v: ['Ovia'] },
    { p: ['meir'], v: ['Meir Brand', 'Meir'] },
    { p: ['nero'], v: ['Nero Brand', 'Nero'] },
    { p: ['fienza'], v: ['Fienza'] },
    { p: ['modern national', 'mn'], v: ['Modern National'] },
    { p: ['adp', 'architectural designer products'], v: ['Architectural Designer Products (ADP)', 'ADP'] },
    { p: ['greens'], v: ['Greens Tapware', 'Greens'] },
    { p: ['aquaperla', 'aqua perla'], v: ['Aquaperla'] },
    { p: ['mercio'], v: ['Mercio'] },
    { p: ['linkware'], v: ['Linkware'] },
    { p: ['hotwire'], v: ['Hotwire Heating'] },
    { p: ['geberit'], v: ['Geberit'] },
    { p: ['linsol'], v: ['Linsol'] },
    { p: ['madu living', 'madu'], v: ['Madu Living'] },
    { p: ['thermogroup'], v: ['Thermogroup'] },
    { p: ['poseidon'], v: ['Poseidon'] },
    { p: ['oliveri'], v: ['Oliveri'] },
    { p: ['metlam'], v: ['Metlam'] },
    { p: ['pietra bianca'], v: ['Pietra Bianca'] },
    { p: ['rubine'], v: ['Rubine'] },
    { p: ['stormtech'], v: ['STORMTECH'] }
  ];

  /* ---- conservative filler strip (greetings/pronouns/intent-verbs/articles) - */
  var FILLER = [
    /\b(hello|hi+|hey|yo|good (morning|afternoon|evening|day)|please|thanks|thank you|cheers|ok|okay)\b/g,
    /\b(i ?m|i am|i|we|we ?re|you|your|my|me|us)\b/g,
    /\b(looking for|look for|searching for|search for|need(ing)?|want(ing)?|would like|like to|trying to find|find me|find|get me|show me|show|do you (have|sell|stock)|have you got|is there|are there|interested in|after)\b/g,
    /\b(a|an|the|some|any|of|for|with|to|that|this|these|those|and|or|just)\b/g,
    /\b(whats?|which|how|why|when|where|who|is|are|am|be|was|were|do|does|did|can|could|would|should|have|has|got)\b/g
  ];

  function normalize(raw) {
    var s = String(raw == null ? '' : raw).toLowerCase().replace(/\s+/g, ' ').trim();
    if (!s) return '';
    s = s.replace(/(\d+)\s*mm\b/g, '$1');                  // 1500mm -> 1500
    for (var p in PHRASE_EXPAND) {                          // gun metal -> gunmetal
      if (PHRASE_EXPAND.hasOwnProperty(p)) s = s.replace(new RegExp('\\b' + p + '\\b', 'g'), PHRASE_EXPAND[p]);
    }
    var before = s;                                         // filler strip (guarded)
    var t = ' ' + s + ' ';
    for (var i = 0; i < FILLER.length; i++) t = t.replace(FILLER[i], ' ');
    t = t.replace(/\s+/g, ' ').trim();
    s = t ? t : before;                                     // never strip to empty
    s = s.split(' ').map(function (w) { return WORD_EXPAND[w] || w; }).join(' ');
    return s.replace(/\s+/g, ' ').trim();
  }

  /* ---- FINISH-FAMILY normalisation -----------------------------------------
   *  ONLY for finishes that are the SAME finish under brand-specific names:
   *  Urban Brass / Tiger Bronze / Brushed Brass = brushed GOLD; Lustre Bronze =
   *  brushed COPPER. The GLOBAL index carries one-way synonyms keyed on a bare
   *  trigger (gold -> brushed gold/tiger bronze/urban brass/brushed brass;
   *  copper -> brushed copper/lustre bronze). A qualifier like "brushed" in the
   *  query is a REQUIRED term that drops family members whose names lack it
   *  ("brushed gold mixer" = 84 vs "gold mixer" = 210), so we map those phrases to
   *  the bare trigger - dropping the meaningless qualifier AND letting the synonym
   *  expand to the family. Theme-only, NO index change.
   *
   *  Deliberately NOT mapped (these are NOT "one finish, many names"):
   *   - BLACK: "matte black" vs "gloss black" are DIFFERENT finishes (sheen), and
   *     the index's "Shadow" is really gunmetal. So we never strip "matte"/"gloss";
   *     "matte black" stays literal and correctly returns matte black only.
   *   - brushed nickel (warm vs cool must stay distinct - see finish nickel cleanup).
   *   - chrome / white / champagne / stainless (no genuine multi-name family).
   *   - antique brass (its own family; bare "brass" left alone so it's never read as gold). */
  var FINISH_NORM = [
    { p: 'brushed gold', t: 'gold' }, { p: 'yellow gold', t: 'gold' },
    { p: 'urban brass', t: 'gold' }, { p: 'brushed brass', t: 'gold' }, { p: 'aged brass', t: 'gold' },
    { p: 'tiger bronze', t: 'gold' },
    { p: 'brushed copper', t: 'copper' }, { p: 'lustre bronze', t: 'copper' }
  ];
  /* longest phrase first so a multi-word name is consumed before any shorter overlap */
  FINISH_NORM.sort(function (a, b) { return b.p.split(' ').length - a.p.split(' ').length; });

  function finishExpand(cleaned) {
    var s = ' ' + String(cleaned == null ? '' : cleaned) + ' ';
    for (var i = 0; i < FINISH_NORM.length; i++) {
      var pat = ' ' + FINISH_NORM[i].p + ' ', rep = ' ' + FINISH_NORM[i].t + ' ';
      while (s.indexOf(pat) !== -1) s = s.replace(pat, rep);
    }
    return s.replace(/\s+/g, ' ').trim();
  }

  /* finish FAMILIES - for UI transparency: what each trigger word represents +
     the brand-specific members it pulls in (mirrors the index family synonyms). */
  var FINISH_FAMILIES = {
    gold:   { label: 'Brushed Gold',   swatch: 'brushed-gold',   incl: ['Brushed Brass', 'Urban Brass', 'Tiger Bronze'] },
    copper: { label: 'Brushed Copper', swatch: 'brushed-copper', incl: ['Lustre Bronze'] }
  };
  /* which finish family (if any) a finish-expanded query resolves to (else null) */
  function detectFinishFamily(cleaned) {
    var t = ' ' + String(cleaned == null ? '' : cleaned).toLowerCase() + ' ';
    for (var k in FINISH_FAMILIES) {
      if (FINISH_FAMILIES.hasOwnProperty(k) && t.indexOf(' ' + k + ' ') !== -1) return k;
    }
    return null;
  }

  function detectBoosts(cleaned) {
    var text = ' ' + String(cleaned == null ? '' : cleaned).toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
    var out = [], seen = {}, i, k, g, pat;
    /* attr defaults to raw product_type; governed entries pass GOV_ATTR */
    function add(types, attr) { for (var n = 0; n < types.length; n++) { var f = (attr || 'product_type') + ':' + types[n] + '<score=2>'; if (!seen[f]) { seen[f] = 1; out.push(f); } } }
    // 1) granular phrases (longest first) - match, boost, and CONSUME the words
    for (i = 0; i < GRANULAR.length; i++) {
      g = GRANULAR[i];
      var hit = false;
      for (k = 0; k < g.p.length; k++) {
        pat = ' ' + g.p[k] + ' ';
        while (text.indexOf(pat) !== -1) { text = text.replace(pat, '  '); hit = true; }
      }
      if (hit) { if (g.g) add(g.g, GOV_ATTR); else add(g.t); }
    }
    // 2) coarse category triggers on whatever words remain
    var toks = text.trim().split(/\s+/), cats = {}, cat;
    for (i = 0; i < toks.length; i++) { cat = TRIGGERS[toks[i]]; if (cat) cats[cat] = 1; }
    for (cat in cats) { if (cats.hasOwnProperty(cat)) { if (CATEGORY_BOOSTS_GOV[cat]) add(CATEGORY_BOOSTS_GOV[cat], GOV_ATTR); else add(CATEGORY_BOOSTS[cat]); } }
    return out;
  }

  /* which brand(s) the query names -> their vendor strings ([] if none) */
  function detectBrand(cleaned) {
    var text = ' ' + String(cleaned == null ? '' : cleaned).toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
    var vendors = [], seen = {}, i, k, j, b, hit;
    for (i = 0; i < BRANDS.length; i++) {
      b = BRANDS[i]; hit = false;
      for (k = 0; k < b.p.length; k++) { if (text.indexOf(' ' + b.p[k] + ' ') !== -1) { hit = true; break; } }
      if (hit) for (j = 0; j < b.v.length; j++) { if (!seen[b.v[j]]) { seen[b.v[j]] = 1; vendors.push(b.v[j]); } }
    }
    return vendors;
  }

  /* ---- SKU-shaped queries (v1.6) -------------------------------------------
   *  In-house staff search by product code ("MIL09CH", "MIL-09CH"). Detection
   *  is deliberately conservative: ONE token, 4-24 chars once separators are
   *  stripped, containing BOTH letters and digits - so no ordinary word and no
   *  bare measurement ("900", already de-mm'd by normalize) ever qualifies.
   *  skuCanon strips separators so dashed/spaced writings of a code compare
   *  equal. The engine only DETECTS; the host theme owns the presentation
   *  (SKU lines on cards, exact-match marking). Ranking is untouched. */
  function skuCanon(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function detectSku(cleaned) {
    var s = String(cleaned == null ? '' : cleaned).trim();
    if (!s || /\s/.test(s)) return null;
    var canon = skuCanon(s);
    if (canon.length < 4 || canon.length > 24) return null;
    if (!/[0-9]/.test(canon) || !/[a-z]/.test(canon)) return null;
    return canon;
  }

  /* ---- ATTRIBUTE INTENTS (v1.7) ---------------------------------------------
   *  The index only searches title/vendor/type/sku/barcode, so a size or a
   *  format that lives in variant names or metafields never matches as text:
   *  "1200 vanity" missed every multi-size product whose title has no size
   *  (Ovia Zurich), and "floor standing vanity" missed products titled
   *  "Freestanding". An intent turns those words into a governed-facet FILTER
   *  instead (width_band / governed Type), and strips them from the text.
   *
   *  INCLUSION RULE (Chris 2026-10-07): an intent may only ADD products, never
   *  lose one that matches today. So every intent also emits an `extras`
   *  request: the ORIGINAL text (number and all) restricted to products that
   *  the filter would exclude (NOT the filter values). Main results + extras =
   *  today's results plus the newly found ones. The two sets are disjoint by
   *  construction, so the host can append extras without de-duplicating.
   *
   *  GATED: a size only becomes a filter when the query also names a category
   *  whose products carry width bands (vanity/cabinet/mirror/bath/basin/sink).
   *  "900 shower screen" or "1200 heated towel rail" are left exactly as they
   *  are - those categories have no band data and a filter would empty them.
   *
   *  Opt-in: only enhance(raw, { intents: true }) applies this. Without the
   *  flag enhance() returns exactly what v1.6 returned. */
  var WB_ATTR = 'meta.custom.width_band.fields.name';
  /* the governed width_band vocabulary (Algolia facet values, 2026-10-07).
     A band added later simply never matches a size here - results stay as
     they would have been, so a stale list degrades safely. */
  var WIDTH_BANDS = ['Under 350mm', '350-400mm', '400mm', '400-450mm', '450mm', '450-500mm', '500mm',
    '500-600mm', '600mm', '600-700mm', 'Over 700mm', '700-900mm', '750mm', '900mm', '900-1200mm',
    '1200mm', 'Over 1200mm', '1300mm', '1400mm', '1500mm', '1600mm', '1700mm', 'Over 1700mm', '1800mm'];
  var SIZED_CATEGORY = /(^| )(vanity|vanities|cabinet|cabinets|mirror|mirrors|bath|baths|tub|tubs|basin|basins|sink|sinks)( |$)/;
  var FLOOR_STANDING = ['floor standing', 'floorstanding', 'floor mounted', 'floor mount'];
  var FLOOR_STANDING_GOV = ['Floor Standing Vanity'];

  /* bands containing width n: exact "Nmm" first, then ranges / over / under */
  function bandsFor(n) {
    var exact = [], other = [], i, b, m;
    for (i = 0; i < WIDTH_BANDS.length; i++) {
      b = WIDTH_BANDS[i];
      if ((m = b.match(/^(\d+)mm$/))) { if (+m[1] === n) exact.push(b); }
      else if ((m = b.match(/^(\d+)-(\d+)mm$/))) { if (n >= +m[1] && n <= +m[2]) other.push(b); }
      else if ((m = b.match(/^Over (\d+)mm$/))) { if (n > +m[1]) other.push(b); }
      else if ((m = b.match(/^Under (\d+)mm$/))) { if (n < +m[1]) other.push(b); }
    }
    return { exact: exact, other: other };
  }
  /* params every EXTRAS request must carry: the index falls back to dropping
     words when nothing matches them all (removeWordsIfNoResults), and under a
     NOT filter that fallback turns "1200 vanity" into every vanity lacking
     the band (199 products, measured). Extras must be strict text matches. */
  var EXTRA_PARAMS = 'removeWordsIfNoResults=none';
  /* hosts that match whole words send this INSTEAD of prefixNone on an extra
     carrying prefixLast (the number is its last word) */
  var EXTRA_PREFIX_LAST = 'queryType=prefixLast';
  function filterValue(attr, v) { return attr + ':"' + String(v).replace(/"/g, '\\"') + '"'; }
  function notAll(attr, values) {
    return values.map(function (v) { return 'NOT ' + filterValue(attr, v); }).join(' AND ');
  }

  function detectIntents(q) {
    var text = ' ' + String(q == null ? '' : q) + ' ';
    var out = { query: String(q == null ? '' : q), facetFilters: [], optionalFilters: [], extras: [], size: null };
    if (!SIZED_CATEGORY.test(text.trim())) return out;
    var sizes = text.match(/ \d{3,4}(?= )/g) || [];
    if (sizes.length === 1) {
      var n = +sizes[0].trim();
      if (n >= 300 && n <= 2400) {
        var b = bandsFor(n), all = b.exact.concat(b.other);
        if (all.length) {
          out.size = n;
          out.facetFilters.push(all.map(function (v) { return WB_ATTR + ':' + v; }));
          for (var i = 0; i < b.exact.length; i++) out.optionalFilters.push(WB_ATTR + ':' + b.exact[i] + '<score=1>');
          /* text side: the number goes LAST so a host running whole-word
             matching (/search) can use queryType=prefixLast - "1500" then
             matches "1500mm", "1500 x 750" and "1500x750" alike, while the
             other words stay whole ("bath" never swallows "Bathroom"). This
             is what keeps size search independent of width_band coverage:
             any product with the size in its title is found either way. */
          var rest = text.replace(sizes[0] + ' ', ' ').replace(/\s+/g, ' ').trim();
          out.extras.push({ query: (rest + ' ' + n).trim(), filters: notAll(WB_ATTR, all), prefixLast: true });
          text = text.replace(sizes[0] + ' ', ' ');
        }
      }
    }
    if (/ (vanity|vanities) /.test(text)) {
      for (var k = 0; k < FLOOR_STANDING.length; k++) {
        var pat = ' ' + FLOOR_STANDING[k] + ' ';
        if (text.indexOf(pat) === -1) continue;
        out.facetFilters.push(FLOOR_STANDING_GOV.map(function (v) { return GOV_ATTR + ':' + v; }));
        out.extras.push({ query: out.query, filters: notAll(GOV_ATTR, FLOOR_STANDING_GOV) });
        while (text.indexOf(pat) !== -1) text = text.replace(pat, ' ');
        break;
      }
    }
    out.query = text.replace(/\s+/g, ' ').trim() || out.query;
    return out;
  }

  /* the variant of a product titled with the searched width ("1200mm Centre
     Bowl / Standard"). variants = Algolia variant records of ONE product.
     Cheapest wins; on a price tie the plainest build wins - not an Express
     Ship variant (it opens the builder in its locked express mode), then a
     Centre bowl. Returns the variant record or null. */
  function variantRank(v) {
    var t = String(v.variant_title || '').toLowerCase();
    return (t.indexOf('express') !== -1 ? 2 : 0) + (t.indexOf('centre') !== -1 ? 0 : 1);
  }
  function variantForSize(variants, n) {
    if (!n || !variants || !variants.length) return null;
    var re = new RegExp('(^|[^0-9])' + n + ' ?mm\\b', 'i'), best = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i], p = +v.price || 0, bp;
      if (!re.test(String(v.variant_title || ''))) continue;
      if (!best) { best = v; continue; }
      bp = +best.price || 0;
      if (p < bp || (p === bp && variantRank(v) < variantRank(best))) best = v;
    }
    return best;
  }

  /* REST params for the follow-up that finds each hit's variant of the
     searched width (one sub-request per multi-variant hit that isn't already
     that size). Returns [{ i: hitIndex, params }]; the host batches them. */
  function sizeVariantRequests(hits, n) {
    var out = [];
    if (!n || !hits) return out;
    for (var i = 0; i < hits.length; i++) {
      var h = hits[i];
      if (!h || !h.id || !(+h.variants_count > 1)) continue;
      if (variantForSize([h], n)) continue;
      out.push({ i: i, params: 'query=&hitsPerPage=250&distinct=false&clickAnalytics=false' +
        '&attributesToHighlight=' + encodeURIComponent('[]') +
        '&attributesToRetrieve=' + encodeURIComponent(JSON.stringify(['objectID', 'variant_title', 'price', 'compare_at_price'])) +
        '&filters=' + encodeURIComponent('_tags:"product:' + h.id + '"') });
    }
    return out;
  }

  function enhance(raw, opts) {
    var n = normalize(raw);
    /* skipFinish: keep the literal finish (don't broaden to the family) - used
       to NARROW to a single finish, e.g. "urban brass" only, not all gold. */
    var q = (opts && opts.skipFinish) ? n : finishExpand(n);
    var of = detectBoosts(q);                              // product_type:...<score=2>
    var brand = detectBrand(q);
    if (brand.length) {                                    // brand named -> boost it, NO Ovia
      for (var i = 0; i < brand.length; i++) of.push('vendor:' + brand[i] + '<score=1>');
    } else {                                               // no brand -> gently promote Ovia
      of.push('vendor:Ovia<score=1>');
    }
    if (!(opts && opts.intents)) return { query: q, optionalFilters: of };
    /* boosts are detected on the FULL text above ("floor standing vanity"
       keeps its governed boost); only the query text sent to Algolia is
       stripped. Extras carry the same boosts so they rank sensibly too. */
    var it = detectIntents(q);
    return {
      query: it.query,
      optionalFilters: of.concat(it.optionalFilters),
      facetFilters: it.facetFilters,
      extras: it.extras,
      size: it.size
    };
  }

  /* Append to a REST param string. vendor boosts (score 1) must SUM with the
     type boosts (score 2), not max - so sumOrFiltersScores must be on, and type
     outranks vendor (right category always beats a same-brand wrong category). */
  function appendOptionalFilters(params, of) {
    if (!of || !of.length) return params;
    return params + '&optionalFilters=' + encodeURIComponent(JSON.stringify(of)) + '&sumOrFiltersScores=true';
  }

  root.BSDSearch = {
    version: '1.7.0',
    enhance: enhance,
    normalize: normalize,
    finishExpand: finishExpand,
    detectFinishFamily: detectFinishFamily,
    FINISH_FAMILIES: FINISH_FAMILIES,
    detectBoosts: detectBoosts,
    detectBrand: detectBrand,
    appendOptionalFilters: appendOptionalFilters,
    detectSku: detectSku,
    skuCanon: skuCanon,
    detectIntents: detectIntents,
    variantForSize: variantForSize,
    sizeVariantRequests: sizeVariantRequests,
    WB_ATTR: WB_ATTR,
    EXTRA_PARAMS: EXTRA_PARAMS,
    EXTRA_PREFIX_LAST: EXTRA_PREFIX_LAST,
    CATEGORY_BOOSTS: CATEGORY_BOOSTS,
    CATEGORY_BOOSTS_GOV: CATEGORY_BOOSTS_GOV,
    GOV_ATTR: GOV_ATTR,
    GRANULAR: GRANULAR,
    BRANDS: BRANDS
  };
})(typeof window !== 'undefined' ? window : this);
